//src/services/surveyResults.ts
import { api } from "@/lib/apiClient";
import type {
  SurveyCampaignResponseSummary,
  SurveyCampaignResults,
  SurveyRebuildAnalyticsOut,
  SurveyResultCut,
} from "@/types/gro";
import type {
  SurveyRebuildAnalyticsIn,
  SurveyRiskSuggestionView,
  AiCampaignSuggestionsOut,
} from "@/types/surveyResults";
import type { AiFeedbackStatus, AiGeneratedOutput, AiGeneratedOutputPage } from "@/types/ai";
import type { PagedResult } from "@/types/surveyRiskSuggestions";

export async function getSurveyCampaignResponseSummary(campaignId: string) {
  return api.get<SurveyCampaignResponseSummary>(`/survey-campaigns/${campaignId}/responses/summary`);
}

export async function getSurveyCampaignResults(campaignId: string) {
  return api.get<SurveyCampaignResults>(`/survey-campaigns/${campaignId}/results`);
}

export async function rebuildSurveyCampaignAnalytics(campaignId: string, payload: SurveyRebuildAnalyticsIn) {
  return api.post<SurveyRebuildAnalyticsOut>(`/survey-campaigns/${campaignId}/analytics/rebuild`, payload);
}

export async function listSurveyCampaignCuts(campaignId: string, params?: { cut_type?: string }) {
  return api.get<SurveyResultCut[]>(`/survey-campaigns/${campaignId}/analytics/cuts`, {
    params: { cut_type: params?.cut_type || undefined },
  });
}

export async function listSurveyRiskSuggestions(
  campaignId: string,
  params?: { cut_type?: string; risk_level_code?: string; page?: number; per_page?: number },
) {
  return api.get<PagedResult<SurveyRiskSuggestionView>>(
    `/survey-campaigns/${campaignId}/risk-suggestions`,
    {
      params: {
        cut_type: params?.cut_type || undefined,
        risk_level_code: params?.risk_level_code || undefined,
        page: params?.page || 1,
        per_page: params?.per_page || 10,
      },
    },
  );
}


export async function downloadSurveyCampaignResponsesXlsx(campaignId: string) {
  return api.download(`/survey-campaigns/${campaignId}/responses/export`);
}

export async function generateSurveyCampaignAiSuggestions(campaignId: string) {
  return api.post<AiCampaignSuggestionsOut>(`/survey-campaigns/${campaignId}/ai/suggestions`, {});
}

export async function listSurveyCampaignAiSuggestions(
  campaignId: string,
  params?: { page?: number; per_page?: number },
) {
  return api.get<AiGeneratedOutputPage>(`/survey-campaigns/${campaignId}/ai/suggestions`, {
    params: {
      page: params?.page || 1,
      per_page: params?.per_page || 10,
    },
  });
}

export async function getSurveyCampaignAiSuggestion(campaignId: string, outputId: string) {
  return api.get<AiGeneratedOutput>(`/survey-campaigns/${campaignId}/ai/suggestions/${outputId}`);
}

export async function rejectSurveyCampaignAiSuggestion(campaignId: string, outputId: string) {
  return api.post<AiGeneratedOutput>(`/survey-campaigns/${campaignId}/ai/suggestions/${outputId}/reject`, {});
}


export async function approveSurveyCampaignAiRisk(campaignId: string, outputId: string, riskIndex: number) {
  return api.post<AiGeneratedOutput>(`/survey-campaigns/${campaignId}/ai/suggestions/${outputId}/risks/${riskIndex}/approve`, {});
}

export async function rejectSurveyCampaignAiRisk(campaignId: string, outputId: string, riskIndex: number) {
  return api.post<AiGeneratedOutput>(`/survey-campaigns/${campaignId}/ai/suggestions/${outputId}/risks/${riskIndex}/reject`, {});
}

export async function resetSurveyCampaignAiRisk(campaignId: string, outputId: string, riskIndex: number) {
  return api.post<AiGeneratedOutput>(`/survey-campaigns/${campaignId}/ai/suggestions/${outputId}/risks/${riskIndex}/reset`, {});
}

export async function undoSurveyCampaignAiRiskApproval(campaignId: string, outputId: string, riskIndex: number) {
  return api.post<AiGeneratedOutput>(`/survey-campaigns/${campaignId}/ai/suggestions/${outputId}/risks/${riskIndex}/undo`, {});
}


export async function updateSurveyCampaignAiSuggestionFeedback(
  campaignId: string,
  outputId: string,
  feedbackStatus: AiFeedbackStatus,
) {
  return api.patch<AiGeneratedOutput>(`/survey-campaigns/${campaignId}/ai/suggestions/${outputId}/feedback`, {
    feedback_status: feedbackStatus,
  });
}
